import type { CompanyMeView, MeView } from '@lp/contracts';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ApiFailure, errorText, get, post } from '@/lib/api';
import { isCompany, useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Button, Card, ErrorBox, Field, Input, PageHeader } from '@/components/ui';

/**
 * Change password. The seeded Super Admin is sent here first (must change); a new password signs every other device out, and this one
 * signs in again with it.
 */
export function AccountPage() {
  const t = useT();
  const nav = useNavigate();
  const session = useAuth((s) => s.session);
  const signedIn = useAuth((s) => s.signedIn);
  const setUser = useAuth((s) => s.setUser);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const must = !!session?.user.mustChangePassword;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== again) return setError(t('The two new passwords are not the same'));
    setBusy(true);
    try {
      const user = session!.user;
      const company = isCompany(user) ? user : null;
      await post(company ? '/company/auth/password' : '/auth/password', { currentPassword: current, newPassword: next });
      // the password change ended every sign-in, this one too: sign in again with the new password
      if (company) {
        signedIn(await post('/company/auth/login', { company: company.client.code, username: company.username, password: next }));
        setUser(await get<CompanyMeView>('/company/auth/me'));
      } else {
        signedIn(await post('/auth/login', { username: user.username, password: next }));
        setUser(await get<MeView>('/auth/me'));
      }
      setDone(true);
      setCurrent('');
      setNext('');
      setAgain('');
      if (must) nav('/', { replace: true });
    } catch (err) {
      // say which box is wrong: "invalid username or password" read as if the NEW password were refused (owner, 2026-09-21)
      const code = err instanceof ApiFailure ? err.body.code : null;
      setError(code === 'VALIDATION' ? t('At least 10 characters, with a letter and a digit') : code === 'INVALID_CREDENTIALS' ? t('The current password is not right') : errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t('Change password')} subtitle={must ? t('Please choose your own password before you continue.') : t('A new password signs you out on every other device.')} />
      <Card className="max-w-md">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <Field label={t('Current password')}>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
          </Field>
          <Field label={t('New password')}>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required minLength={10} />
          </Field>
          <Field label={t('New password again')}>
            <Input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" required minLength={10} />
          </Field>
          <p className="text-xs text-ink-500">{t('At least 10 characters, with a letter and a digit')}</p>
          {error && <ErrorBox text={error} />}
          {done && <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{t('Password changed.')}</div>}
          <Button type="submit" disabled={busy}>
            {t('Change password')}
          </Button>
        </form>
      </Card>
    </>
  );
}
